import React from 'react';
import {Box, Typography, IconButton, Paper, Collapse, lighten} from '@mui/material';
import {useTheme} from '@emotion/react';
import {tokens} from '../theme';
import UnitCard from './UnitCard';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import UpdateIcon from '@mui/icons-material/Update';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NoteAddIcon from '@mui/icons-material/NoteAdd';

const DisciplineCard = ({
                            discipline,
                            units = [],
                            onEdit,
                            onDelete,
                            onCreateUnit,
                            onClick,
                            onEditUnit,
                            onDeleteUnit,
                            onClickUnit
                        }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // Extract data from discipline
    const {
        name = '',
        discipline_id,
        color = '#2196F3',  // Default to blue if no color provided
        createdAt,
        updatedAt
    } = discipline || {};

    // Use the units from props if provided, otherwise use the ones from discipline
    const disciplineUnits = units.length > 0 ? units : (discipline?.units || []);

    // Format dates if available
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        } catch (error) {
            return '';
        }
    };

    const createdDate = formatDate(createdAt);
    const updatedDate = formatDate(updatedAt);

    const hasUnits = disciplineUnits.length > 0;
    const unitCount = disciplineUnits.length;
    const skillCount = disciplineUnits.reduce((total, unit) => total + (unit.skills?.length || 0), 0);

    return (
        <Paper
            elevation={0}
            sx={{
                border: `1px solid ${colors.grey[300]}`,
                borderRadius: '8px',
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                },
                backgroundColor: lighten(color, 0.7),
                borderColor: lighten(color, 0.2),
                cursor: 'pointer',
                minHeight: hasUnits ? '60px' : '120px',
            }}
            onClick={() => onClick && onClick(discipline)}
        >

            {/* Header with title and actions */}
            <Box
                sx={{
                    p: 1,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '100%'
                }}
            >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    {/* Color indicator */}
                    <Box
                        sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '50%',
                            bgcolor: color,
                            flexShrink: 0
                        }}
                    />

                    {/* Folder Icon */}
                    <FolderOutlinedIcon sx={{
                        color: colors.grey[850],
                        fontSize: '20px',
                        flexShrink: 0
                    }}/>

                    <Box>
                        {/* Title */}
                        <Typography
                            variant="h6"
                            sx={{
                                fontSize: '1.3rem',
                                fontWeight: 700,
                                lineHeight: 1.4
                            }}
                        >
                            {name}
                        </Typography>

                        {/* Subtitle with counts and dates */}
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                            <Typography
                                variant="body2"
                                color={colors.grey[500]}
                                sx={{fontSize: '0.85rem'}}
                            >
                                {unitCount} unit(s)
                            </Typography>

                            {/* Date information with icons */}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                {createdDate && (
                                    <Box sx={{display: 'flex', alignItems: 'center'}}>
                                        <AccessTimeIcon sx={{color: colors.grey[600], fontSize: '0.75rem', mr: 0.5}}/>
                                        <Typography variant="caption" color={colors.grey[600]}
                                                    sx={{fontSize: '0.7rem'}}>
                                            {createdDate}
                                        </Typography>
                                    </Box>
                                )}

                                {createdDate && updatedDate && updatedDate !== createdDate && (
                                    <>
                                        <FiberManualRecordIcon sx={{color: colors.grey[600], fontSize: '0.4rem'}}/>
                                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                                            <UpdateIcon sx={{color: colors.grey[600], fontSize: '0.75rem', mr: 0.5}}/>
                                            <Typography variant="caption" color={colors.grey[600]}
                                                        sx={{fontSize: '0.7rem'}}>
                                                {updatedDate}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Box>

                {/* Action buttons */}
                <Box sx={{display: 'flex'}}>
                    {/* Create Unit button */}
                    <IconButton
                        size="small"
                        sx={{
                            color: colors.grey[650],
                            padding: '4px',
                            '&:hover': {
                                color: colors.primary[500]
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCreateUnit && onCreateUnit(discipline);
                        }}
                        title="Create new unit"
                    >
                        <NoteAddIcon sx={{fontSize: '18px'}}/>
                    </IconButton>

                    {/* Edit button */}
                    <IconButton
                        size="small"
                        sx={{
                            color: colors.grey[650],
                            padding: '4px',
                            '&:hover': {
                                color: colors.primary[500]
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEdit && onEdit(discipline);
                        }}
                        title="Edit discipline"
                    >
                        <EditIcon sx={{fontSize: '18px'}}/>
                    </IconButton>

                    {/* Delete button */}
                    <IconButton
                        size="small"
                        sx={{
                            color: colors.grey[650],
                            padding: '4px',
                            '&:hover': {
                                color: colors.redAccent[500]
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete && onDelete(discipline);
                        }}
                        title="Delete discipline"
                    >
                        <DeleteIcon sx={{fontSize: '18px'}}/>
                    </IconButton>
                </Box>
            </Box>

            {/* Content for unit list */}
            <Collapse in={true}>
                <Box sx={{px: 1.5, pb: 1.5, pt: hasUnits ? 0 : 1.5}}>
                    {!hasUnits ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                flex: 1,
                                position: 'relative',
                                flexDirection: {xs: 'column', sm: 'row'},
                                gap: 2
                            }}
                        >
                            {/* Empty state */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    py: 3
                                }}
                            >
                                <DescriptionOutlinedIcon
                                    sx={{
                                        color: colors.grey[300],
                                        fontSize: '2.5rem',
                                        mb: 1
                                    }}
                                />

                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: colors.grey[500],
                                        fontWeight: 500
                                    }}
                                >
                                    No units in this discipline
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color={colors.grey[400]}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 1
                                    }}
                                >
                                    <NoteAddIcon sx={{color: colors.grey[400], fontSize: '0.9rem', mr: 0.5}}/>
                                    Click the add icon to create a new unit
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1, mt: 1}}>
                            {disciplineUnits.map(unit => (
                                <UnitCard
                                    key={unit.unit_id}
                                    unit={unit}
                                    onEdit={() => onEditUnit && onEditUnit(unit)}
                                    onDelete={() => onDeleteUnit && onDeleteUnit(unit)}
                                    onClick={() => onClickUnit && onClickUnit(unit)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );
};

export default DisciplineCard;
